import * as types from './ActionTypes';
import axios from 'utils/axios';
import { API_URL } from 'configs';

const loader = label => ({ type: types.SHOW_LOADER, common_loader_label: label });
const hideLoader = () => ({ type: types.HIDE_LOADER });

const snackbar = (msg, type = 'general_error') => ({
  type: types.SHOW_SNACKBAR,
  snackbar_notification: msg,
  snackbar_notification_type: type,
});

const getErrorMessage = error => {
  const res = error?.response;
  return res?.data?.error || `${res?.status || ''} ${res?.statusText || ''}`.trim() || 'Something went wrong';
};

const reportRequest = (endpoint, data = {}, requestType, successType, failureType) => dispatch => {
  dispatch({ type: requestType });
  dispatch(loader());

  axios
    .post(`${API_URL}${endpoint}`, data, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
    .then(res => {
      dispatch(hideLoader());
      dispatch({
        type: successType,
        payload: res.data,
      });
    })
    .catch(error => {
      dispatch(hideLoader());
      dispatch({
        type: failureType,
        payload: getErrorMessage(error),
      });
      dispatch(snackbar(getErrorMessage(error)));
    });
};

export const getMonthlyProgramTimesheet = (data = {}) =>
  reportRequest(
    'timesheet/reports/monthly-program-timesheet',
    data,
    types.GET_MONTHLY_PROGRAM_TIMESHEET_REQUEST,
    types.GET_MONTHLY_PROGRAM_TIMESHEET_SUCCESS,
    types.GET_MONTHLY_PROGRAM_TIMESHEET_FAILURE
  );

export const clearMonthlyProgramTimesheet = () => dispatch => {
  dispatch({ type: types.CLEAR_MONTHLY_PROGRAM_TIMESHEETS_REPORT });
};

export const getMonthlyUserTimesheet = (data = {}) =>
  reportRequest(
    'timesheet/reports/monthly-user-timesheet',
    data,
    types.GET_MONTHLY_USER_TIMESHEET_REQUEST,
    types.GET_MONTHLY_USER_TIMESHEET_SUCCESS,
    types.GET_MONTHLY_USER_TIMESHEET_FAILURE
  );

export const clearMonthlyUserTimesheet = () => dispatch => {
  dispatch({ type: types.CLEAR_MONTHLY_USER_TIMESHEETS_REPORT });
};

export const getWeeklySummaryReport = (data = {}) =>
  reportRequest(
    'timesheet/reports/weekly-summary-report',
    data,
    types.GET_WEEKLY_SUMMARY_TIMESHEET_REQUEST,
    types.GET_WEEKLY_SUMMARY_TIMESHEET_SUCCESS,
    types.GET_WEEKLY_SUMMARY_TIMESHEET_FAILURE
  );

export const clearWeeklySummaryReport = () => dispatch => {
  dispatch({ type: types.CLEAR_WEEKLY_SUMMARY_TIMESHEETS_REPORT });
};

export const getMonthlyDetailedTimesheetReport = (data = {}) =>
  reportRequest(
    'timesheet/reports/monthly-detailed-timesheet-report',
    data,
    types.GET_MONTHLY_DETAILED_TIMESHEET_REQUEST,
    types.GET_MONTHLY_DETAILED_TIMESHEET_SUCCESS,
    types.GET_MONTHLY_DETAILED_TIMESHEET_FAILURE
  );

export const clearMonthlyDetailedTimesheetReport = () => dispatch => {
  dispatch({ type: types.CLEAR_MONTHLY_DETAILED_TIMESHEETS_REPORT });
};

export const getTimesheetCategorySummaryReport = (data = {}) =>
  reportRequest(
    'timesheet/reports/task-summary-report',
    data,
    types.GET_CATEGORY_SUMMARY_REPORT_REQUEST,
    types.GET_CATEGORY_SUMMARY_REPORT_SUCCESS,
    types.GET_CATEGORY_SUMMARY_REPORT_FAILURE
  );

export const clearTimesheetCategorySummaryReport = () => dispatch => {
  dispatch({ type: types.CLEAR_CATEGORY_SUMMARY_REPORT });
};

export const getAccountManagerMonthlyReport = (data = {}) =>
  reportRequest(
    'timesheet/reports/account-manager-monthly-report',
    data,
    types.GET_ACCOUNT_MANAGER_MONTHLY_REPORT_REQUEST,
    types.GET_ACCOUNT_MANAGER_MONTHLY_REPORT_SUCCESS,
    types.GET_ACCOUNT_MANAGER_MONTHLY_REPORT_FAILURE
  );

export const clearAccountManagerMonthlyReport = () => dispatch => {
  dispatch({ type: types.CLEAR_ACCOUNT_MANAGER_MONTHLY_REPORT });
};

const parseBlobErrorMessage = async (error, fallbackMessage) => {
  const res = error?.response;
  const data = res?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      if (json.error) return json.error;
      if (json.message) return json.message;
      if (json.errors) {
        const fieldErrors = Object.values(json.errors).flat().filter(Boolean);
        if (fieldErrors.length) return fieldErrors.join(', ');
      }
    } catch (e) {
      // Response was not JSON
    }
  }

  return getErrorMessage(error) || fallbackMessage;
};

export const exportTaskTimesheetReports = (payload, filename) => async dispatch => {
  dispatch(loader('Generating task reports...'));

  try {
    const response = await axios.post(
      `${API_URL}timesheet/reports/task-timesheet-reports/export`,
      payload,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      let message = 'Unable to export task reports.';
      try {
        const json = JSON.parse(text);
        message = json.error || json.message || message;
        if (json.errors) {
          const fieldErrors = Object.values(json.errors).flat().filter(Boolean);
          if (fieldErrors.length) message = fieldErrors.join(', ');
        }
      } catch (e) {
        // Response was not JSON
      }
      dispatch(hideLoader());
      dispatch(snackbar(message));
      return;
    }

    const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = urlBlob;
    const contentDisposition = response.headers['content-disposition'];
    let downloadName = filename;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        downloadName = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    link.setAttribute('download', downloadName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(urlBlob);
    dispatch(hideLoader());
  } catch (error) {
    dispatch(hideLoader());
    const message = await parseBlobErrorMessage(error, 'Unable to export task reports.');
    dispatch(snackbar(message));
    throw error;
  }
};
